// ═══════════════════════════════════════════════════════════════════
// beingMotion.js — THE BESTIARY · H24 · Terror TCG
// Twelve species Lyt Attacks · 36 summons · 4 Esclings · the Rite of
// Return. Everything a BEING does that isn't wearing a brand.
// ═══════════════════════════════════════════════════════════════════
//
// ── WHAT MOVED HERE AND WHY NOTHING CHANGED ────────────────────────
// The twelve Lyt Attacks and the Rite of Return were authored in H23
// Ship 1, shipped, and signed off. They are reproduced here VERBATIM —
// same cues, same numbers, same comments — because H24 needed one home
// per card class and `cardMotion.js` became the hub that routes to
// them. This was a filing job, not a rewrite. Do not "improve" a score
// in this file that caP'n has already accepted without him asking.
//
// ── THE KEYING LAW, RESTATED BECAUSE IT IS EASY TO GET BACKWARD ────
// caP'n, July 27: "let's give each species its own Lyt attack so Shadow
// Fox Beb, champion, and Lord share their Lyt Attack animation."
//
//   LYT ATTACK  keyed by SPECIES  — 12 authored, shared across 3 levels
//   SUMMON      keyed by CARD     — 36 authored, one per level
//
// Those two lines look inconsistent and they are exactly right. A
// Shadow Fox Beb and a Shadow Fox Lord FIGHT the same way — the Lord is
// simply better at it, and H21's `power` term already scales the swing.
// But they do not ARRIVE the same way, because the summon is the one
// moment where the Level IS the character. A spoiled kitten and the
// King of the Spirit Worlds take a rift differently, and H23 proved
// that on the Spectral Tiger line before the other eleven were written.
//
// ── THE THREE ARRIVALS ─────────────────────────────────────────────
// So the 36 are built as one shape at three weights, with each species
// putting its own voice on top:
//
//   BEB       ~740ms   it condenses. Small, quick, unbothered.
//   CHAMPION  ~900ms   it arrives ALREADY WORKING — the tell is that a
//                      Champion does something on landing that a Beb
//                      does not: it opens marks at nothing, it scans,
//                      it tests the ground.
//   LORD     ~1200ms   the world opens for it. Every Lord gets the
//                      pillar and the second, wider ring. Nothing below
//                      Lord is allowed either one, which is what makes
//                      a Lord landing feel like an event three months
//                      into playing this game.
//
// That escalation is the whole reason to author 36 instead of 12: a
// player learns to read the rank of a being before they have read its
// name, from across the table, at a glance.
// ═══════════════════════════════════════════════════════════════════

import {
  P, score, toll, ilkHue, beam, orb,
  slash, shock, motes, pillar, spiral, bolt,
  wave, bloom, stream, glyph, grave, rune,
  veil, shatter, coin, crack, embers, lattice,
  halo, star, rift, sever, plume, thorn
} from "./motionKit";

const S = { to: "self" };

// ═══════════════════════════════════════════════════════════════════
// PART I — THE SPECIES KEY
// ═══════════════════════════════════════════════════════════════════
// One regex, and it is the reason the attack table is 12 entries
// instead of 36. Anything that doesn't match (an Escling, a card minted
// next year) returns its own id and finds no entry — a clean miss
// straight down to the taxonomy floor, never a throw.

export const lineOf = (card) => {
  const id = typeof card === "string" ? card : card?.id;
  if (!id) return null;
  // NECRO SEAL HARDENING (2026-08-18, loose debt paid): a ruined
  // being is sealed per copy — `taez_beb~gen~seq` — and KEEPS the
  // tag when Resurrected back onto the field. An end-anchored
  // suffix strip never fires on a sealed id, so the registry lookup
  // missed and a returned H-01 fell to the taxonomy floor for the
  // rest of its life. Strip the seal first: "~" is the seal's own
  // separator (chosen Firebase-key-safe) and appears in no bare id.
  return id.split("~")[0].replace(/_(beb|champion|lord)$/, "");
};

// ═══════════════════════════════════════════════════════════════════
// PART II — THE TWELVE ATTACKS  (H23 Ship 1 · unchanged)
// ═══════════════════════════════════════════════════════════════════
// Read the flavour text, then read the score. Every one of these was
// written off the card's own printed line, not off its Ilk — that is
// the difference caP'n is paying for.

export const LYT_ATTACKS = {

  // ── H-01 · STARFALL ─────────────────────────────────────────────
  // Blue Giant → Quasar → Super Nova: the line is named for the stages
  // of a dying star, so the attack is a star ARRIVING. Nothing crosses
  // the table; a column of light simply lands on the victim. No travel
  // at all — H-01 talks first and then the sky answers.
  h01: score("Starfall", [
    pillar(0, P.starWhite, { ms: 560, r: 5.4, size: 0.52, spin: 0.5 }),
    bloom(190, P.starCore, { ms: 380, r: 1.35 }),
    shock(220, P.starWhite, { ms: 540, size: 0.25, r: 2.1 }),
    motes(250, P.starCore, { ms: 480, n: 24, r: 1.5, size: 0.07 }),
  ], 860),

  // ── H-09 · BLASTER VOLLEY ───────────────────────────────────────
  // "Star level blasters that can decimate most Champions in seconds."
  // Militancy as a first option, per the Beb's own line. Three rounds,
  // 110ms apart, each flowering on arrival. The only attack in the game
  // that reads as trigger discipline.
  h09: score("Blaster Volley", [
    orb(0, P.cyanHot, { from: "self", ms: 320, size: 0.115, r: 0.34 }),
    orb(110, P.cyanHot, { from: "self", ms: 320, size: 0.115, r: 0.46 }),
    orb(220, P.space, { from: "self", ms: 320, size: 0.135, r: 0.28 }),
    bloom(300, P.cyanHot, { ms: 220, r: 0.8 }),
    bloom(410, P.cyanHot, { ms: 220, r: 0.9 }),
    bloom(520, P.space, { ms: 300, r: 1.3 }),
    shock(540, P.space, { ms: 460, size: 0.2, r: 1.6 }),
  ], 880),

  // ── XULAN · THE INCISION ────────────────────────────────────────
  // Wanderer → SURGEON → Shadow. Assassins drifting the Void in search
  // of revenge do not rake. They cut once, they cut thin, and the wound
  // opens a beat AFTER the blade has already gone. The quietest attack
  // in the game and by some distance the most unpleasant.
  xulan: score("The Incision", [
    beam(0, P.scalpel, { from: "self", ms: 200, size: 0.022 }),
    slash(215, P.scalpel, { ms: 470, n: 1, r: 1.1, tone: 0 }),
    bloom(220, P.space, { ms: 260, r: 0.65 }),
    motes(300, P.scalpel, { ms: 380, n: 10, r: 0.7, size: 0.05 }),
  ], 760),

  // ── SPECTRAL TIGER · THE POUNCE ─────────────────────────────────
  // A Phantom cat does not run at you — it stops being over there. The
  // body dissolves where it stood, ONE phantom leap crosses the seam,
  // three claws open across the victim, and it re-condenses back in its
  // own rift before the marks have finished bleeding.
  spectral_tiger: score("The Pounce", [
    motes(0, P.phantom, { ...S, ms: 320, n: 22, r: 0.95, size: 0.07 }),
    orb(90, P.phantom, { from: "self", ms: 280, size: 0.1, r: 0.95 }),
    slash(350, P.violetPale, { ms: 540, n: 3, r: 1.08, tone: 26 }),
    bloom(360, P.phantom, { ms: 300, r: 1.0 }),
    shock(390, P.phantom, { ms: 480, size: 0.2, r: 1.6, tone: 0.55 }),
    motes(430, P.violetPale, { ...S, ms: 400, n: 18, r: 0.95, size: 0.07, tone: "in" }),
  ], 960),

  // ── SHADOW FOX · NINE SHADOWS ───────────────────────────────────
  // "Skulking the Shadow Forest, these fox prey on those who seek it."
  // The joke is that you cannot tell which direction it came from: the
  // dark converges from everywhere at once and only THEN is there a
  // bite. `tone:"in"` is doing all the work — the same primitive that
  // makes the Tiger dissolve makes the Fox arrive.
  shadow_fox: score("Nine Shadows", [
    motes(0, P.amethyst, { ms: 540, n: 38, r: 2.9, size: 0.08, tone: "in" }),
    bloom(440, P.violetDeep, { ms: 300, r: 1.1 }),
    slash(460, P.violetPale, { ms: 400, n: 2, r: 0.82, tone: 42 }),
    shock(490, P.amethyst, { ms: 400, size: 0.18, r: 1.3, tone: 0.6 }),
  ], 900),

  // ── INVERTED SERPENT · THE COIL ─────────────────────────────────
  // Its Lord Impera is Inverted Scales and its U'hlti is Inverted
  // Fangs — the whole species is named for reversal and constriction,
  // so the score is a ribbon that winds twice, tightens, and only bites
  // once the coil has closed. Two punctures, eight degrees apart:
  // fangs, not claws.
  inverted_serpent: score("The Coil", [
    spiral(0, P.amethyst, { ms: 640, spin: 2.4, r: 0.88, size: 1.15 }),
    slash(500, P.violetIce, { ms: 360, n: 2, r: 0.56, tone: 8 }),
    bloom(510, P.phantom, { ms: 260, r: 0.7 }),
    shock(540, P.violetDeep, { ms: 380, size: 0.16, r: 1.15, tone: 0.6 }),
  ], 940),

  // ── Z4Z · THE SUNDERING ─────────────────────────────────────────
  // Demon → Wizard → H'ehll's Sword, and its Imperas are literally
  // Relentless, Catastrophe and Apocalypse. A Time machine that
  // destroys for money does not aim: it tears the space between, twice,
  // and the second tear is the echo of the first arriving late.
  z4z: score("The Sundering", [
    bolt(0, P.time, { from: "self", ms: 260, r: 0.34 }),
    bolt(155, P.emberGold, { from: "self", ms: 230, r: 0.2 }),
    bloom(250, P.time, { ms: 320, r: 1.25 }),
    shock(270, P.time, { ms: 480, size: 0.22, r: 1.9 }),
    motes(285, P.emberGold, { ms: 420, n: 20, r: 1.3, size: 0.07 }),
  ], 780),

  // ── DG-0 · THE VERDICT ──────────────────────────────────────────
  // Angel → Farmer → I'nfini's Shield, Imperas named Hope, Law and
  // Order, Justice Prevails. The only attack in the game that CLOSES
  // before it strikes: an iris of gold contracts onto the victim (the
  // shock primitive run backward — size > r), the judgment lands, and
  // only then does the light come down.
  dg0: score("The Verdict", [
    shock(0, P.emberGold, { ms: 500, size: 2.6, r: 0.55, tone: 0.9 }),
    glyph(400, P.judgeGold, { ms: 520, tone: "✦", size: 34, r: 0.5 }),
    pillar(420, P.judgeGold, { ms: 460, r: 4.4, size: 0.42, spin: 0.2 }),
    bloom(450, P.emberGold, { ms: 320, r: 1.2 }),
    shock(520, P.time, { ms: 420, size: 0.2, r: 1.5, tone: 0.55 }),
  ], 960),

  // ── ANTDROID · THE SWARM ────────────────────────────────────────
  // "The most common Beb in the Unstable World, they only seek Anima
  // and will eat without bias." Not a strike, a COLUMN — a continuous
  // line of small things crossing the gap and taking what it finds. The
  // stream primitive exists for this card and this card named it.
  antdroid: score("The Swarm", [
    stream(0, P.time, { from: "self", ms: 640, n: 56, r: 0.24, size: 0.06 }),
    bloom(490, P.swarm, { ms: 280, r: 0.95 }),
    shock(510, P.time, { ms: 400, size: 0.18, r: 1.4, tone: 0.6 }),
    motes(520, P.swarm, { ms: 340, n: 16, r: 1.0, size: 0.055 }),
  ], 880),

  // ── LUCKY DRAGON · THE WAGER ────────────────────────────────────
  // "These dealers will grant almost any wish, but at what cost?" Every
  // Impera on this line is a coin flip; its U'hlti rolls dice. So the
  // attack is a BET: a die spins over the seam, and only after it lands
  // does anything actually cross. The one attack whose first beat is
  // not aimed at anybody.
  lucky_dragon: score("The Wager", [
    glyph(0, P.swarm, { to: "seam", ms: 560, tone: "⚄", size: 46, spin: 3, r: 0.75 }),
    orb(400, P.swarm, { from: "seam", ms: 260, size: 0.14, r: 0.4 }),
    bloom(620, P.emberGold, { ms: 300, r: 1.2 }),
    motes(630, P.emberGold, { ms: 420, n: 28, r: 1.5, size: 0.075 }),
    shock(640, P.swarm, { ms: 440, size: 0.2, r: 1.7 }),
  ], 980),

  // ── PREMIER DRAGON · THE DECREE ─────────────────────────────────
  // "when born, the surrounding area is briefly affected by Bloom," and
  // Zavixu "deemed these dragons Worthy through rigorous trials."
  // Sovereignty, not savagery. It does not touch the victim: a front
  // crosses the entire field, and whatever it passes over is simply
  // found guilty of being in the way. The only Lyt Attack aimed at the
  // BOARD.
  premier_dragon: score("The Decree", [
    wave(0, P.roseSoft, { from: "self", ms: 640, r: 5.2, size: 0.55, tone: 0.8 }),
    bloom(450, P.dragon, { ms: 340, r: 1.45 }),
    shock(470, P.roseSoft, { ms: 520, size: 0.24, r: 2.3 }),
    glyph(480, P.dragon, { ms: 400, tone: "✦", size: 30, r: 0.45 }),
  ], 900),

  // ── CHAOS DRAGON · THE CALAMITY ─────────────────────────────────
  // "these Bebs bring with their birth a calamity of destruction in the
  // form of Doom." The loudest score in the bestiary and the only one
  // that rings twice — the second ring is wider and quieter, which is
  // what an aftermath sounds like.
  chaos_dragon: score("The Calamity", [
    spiral(0, P.crimson, { tone: "up", ms: 660, spin: 3, r: 0.72, size: 2.9 }),
    motes(400, P.roseSoft, { ms: 540, n: 42, r: 2.0, size: 0.085 }),
    bloom(420, P.crimson, { ms: 340, r: 1.7 }),
    shock(440, P.crimson, { ms: 520, size: 0.26, r: 2.7 }),
    shock(560, P.roseSoft, { ms: 560, size: 0.4, r: 3.6, tone: 0.45 }),
  ], 1120),
};

// ═══════════════════════════════════════════════════════════════════
// PART III — THE THIRTY-SIX ARRIVALS
// ═══════════════════════════════════════════════════════════════════
// One per card. The three weights are functions, so a Lord cannot
// accidentally arrive on Champion machinery and a Beb cannot
// accidentally get a pillar — the rank is enforced by which builder you
// call, not by remembering.

/** BEB — it condenses. Small, quick, and completely unbothered. */
const beb = (name, hue, accent, own = []) => score(name, [
  motes(0, hue, { ...S, ms: 420, n: 24, r: 1.35, size: 0.07, tone: "in" }),
  bloom(320, accent, { ...S, ms: 300, r: 0.8 }),
  shock(360, hue, { ...S, ms: 340, size: 0.16, r: 1.0, tone: 0.55 }),
  ...own,
], 740);

/** CHAMPION — it arrives already working. The `own` cues are the work. */
const champion = (name, hue, accent, own = []) => score(name, [
  motes(0, hue, { ...S, ms: 400, n: 28, r: 1.7, size: 0.075, tone: "in" }),
  bloom(300, accent, { ...S, ms: 280, r: 0.95 }),
  shock(350, hue, { ...S, ms: 420, size: 0.18, r: 1.6, tone: 0.6 }),
  ...own,
], 900);

/** LORD — the world opens. Pillar and the second ring, Lords only. */
const lord = (name, hue, accent, own = []) => score(name, [
  pillar(0, hue, { ...S, ms: 660, r: 5.6, size: 0.5, spin: 0.6 }),
  motes(60, accent, { ...S, ms: 540, n: 42, r: 2.7, size: 0.08, tone: "in" }),
  shock(430, hue, { ...S, ms: 500, size: 0.22, r: 2.6, tone: 0.75 }),
  ...own,
  shock(580, hue, { ...S, ms: 560, size: 0.4, r: 3.5, tone: 0.42 }),
], 1200);

export const SUMMONS = {

// ── SPACE · HUMANOID ───────────────────────────────────────────────

  // "These beings choose words as their first resort." The gentlest
  // arrival of the twelve Bebs — it does not even fully condense, it
  // just becomes present.
  // ⚠ These two were the ONLY colour-only scores left in the summon
  // table — same shape, same timing, different hue. An audit caught
  // them; a player never would have, and that is worse, not better.
  //
  // "A variant of the H-0 ilk, these beings choose words as their first
  // resort." A Blue Giant is a star at the start of its life: it does
  // not arrive, it KINDLES. One ember bloom, and that is the whole
  // difference between this and the Fox below.
  h01_beb: beb("A Giant Kindles", P.starCore, P.starWhite, [
    embers(300, P.starWhite, { ...S, ms: 420, n: 14, r: 0.4, size: 0.06, size2: 1.1 }),
  ]),
  // "Superior suits capable of defending against even Champion level
  // threats." The Champion's work is its ARMOUR coming online.
  h01_champion: champion("The Quasar Wakes", P.starCore, P.starWhite, [
    lattice(360, P.scalpel, { ...S, ms: 520, r: 0.62, n: 12, size: 0.04 }),
  ]),
  // "Souls fused with technology; this is H-01's greatest achievement."
  // A Lord of this line does not walk in — it IGNITES.
  h01_lord: lord("Souls Fused With Light", P.starWhite, P.starCore, [
    star(450, P.starWhite, { ...S, ms: 460, r: 2.4, size: 0.06 }),
  ]),

  // "Militancy as a first option." H-09 arrives armed, at every rank.
  h09_beb: beb("A Star Collapses", P.cyanHot, P.starCore, [
    star(340, P.cyanHot, { ...S, ms: 320, r: 1.0, size: 0.04 }),
  ]),
  h09_champion: champion("The Pulsar Sweeps", P.cyanHot, P.space, [
    star(360, P.cyanHot, { ...S, ms: 340, r: 1.2, size: 0.045, tone: 24 }),
    star(440, P.starCore, { ...S, ms: 340, r: 1.2, size: 0.045, tone: -24 }),
  ]),
  // "The stars bend to these beings' will."
  h09_lord: lord("The Stars Bend", P.cyanHot, P.starWhite, [
    wave(440, P.space, { from: "self", to: "target", ms: 540, r: 4.8, size: 0.44, tone: 0.6 }),
  ]),

  // "A race of assassins that drift through the Void." The Xulan line
  // is the only one that arrives through a RIFT rather than out of
  // motes — they do not condense, they step out of somewhere else.
  xulan_beb: beb("Out of the Void", P.space, P.scalpel, [
    rift(200, P.scalpel, { ...S, ms: 480, r: 1.2, size: 0.03, size2: 0.24 }),
  ]),
  // "Guardians of their weakest territories, they almost never
  // experience battle." The Surgeon lands and tests its blade on
  // nothing — the Champion tell, in its most literal form.
  xulan_champion: champion("The Surgeon Arrives", P.space, P.scalpel, [
    rift(180, P.scalpel, { ...S, ms: 520, r: 1.4, size: 0.032, size2: 0.26 }),
    sever(520, P.scalpel, { ...S, ms: 420, r: 1.1, size: 0.026, size2: 0.16, tone: -10 }),
  ]),
  // "Hate reverts the Surgeons to what they once were before their
  // galaxy was destroyed by H-01."
  xulan_lord: lord("What They Once Were", P.space, P.scalpel, [
    rift(300, P.violetDeep, { ...S, ms: 620, r: 2.0, size: 0.042, size2: 0.36 }),
    sever(500, P.scalpel, { ...S, ms: 480, r: 1.5, size: 0.03, size2: 0.22, tone: -14 }),
  ]),

// ── PHANTOM · BEAST ────────────────────────────────────────────────
// The three Spectral Tiger summons are H23 Ship 1's, verbatim. They are
// written out longhand rather than through the builders above because
// they PREDATE the builders — and reshaping an approved score to fit a
// helper is exactly the kind of silent drift this file warns about.

  // "These spoiled kittens are found only in the Lofty Kingdoms of the
  // Spirit Worlds." Small, soft, entirely unbothered. It doesn't land —
  // it condenses, decides the rift is acceptable, and stays.
  spectral_tiger_beb: score("A Kitten Condenses", [
    motes(0, P.phantom, { ...S, ms: 440, n: 26, r: 1.45, size: 0.07, tone: "in" }),
    bloom(350, P.violetPale, { ...S, ms: 300, r: 0.85 }),
    shock(390, P.phantom, { ...S, ms: 360, size: 0.16, r: 1.05, tone: 0.55 }),
    glyph(380, P.violetPale, { ...S, ms: 380, tone: "✧", size: 24, r: 0.4 }),
  ], 760),

  // "Prowling the Shadow Forest... an insatiable appetite for their own
  // school." The Champion arrives already hunting: it takes the rift and
  // immediately opens two marks in the air in front of it, at nothing.
  // That is the tell — this one eats its own kind.
  spectral_tiger_champion: score("The Prowl", [
    motes(0, P.phantom, { ...S, ms: 400, n: 28, r: 1.75, size: 0.07, tone: "in" }),
    bloom(310, P.violetPale, { ...S, ms: 280, r: 0.95 }),
    slash(320, P.violetPale, { ...S, ms: 460, n: 2, r: 1.0, tone: 30 }),
    shock(360, P.phantom, { ...S, ms: 440, size: 0.18, r: 1.7, tone: 0.6 }),
  ], 880),

  // "Kings of the Spirit Worlds." A Lord does not sneak in. The Spirit
  // World opens a column over the rift, the dark walks up into it, and
  // it signs the arrival with three marks and a second ring the whole
  // table can feel.
  spectral_tiger_lord: score("The King Takes the Rift", [
    pillar(0, P.phantom, { ...S, ms: 660, r: 5.6, size: 0.5, spin: 0.6 }),
    motes(60, P.violetPale, { ...S, ms: 540, n: 42, r: 2.7, size: 0.08, tone: "in" }),
    shock(430, P.phantom, { ...S, ms: 500, size: 0.22, r: 2.6 }),
    slash(450, P.violetPale, { ...S, ms: 540, n: 3, r: 1.3, tone: 24 }),
    glyph(470, P.violetPale, { ...S, ms: 560, tone: "◈", size: 42, r: 0.7 }),
    shock(580, P.violetDeep, { ...S, ms: 560, size: 0.4, r: 3.5, tone: 0.42 }),
  ], 1200),

  // "Mischievous pups from birth, born at the Bottomless Depths of the
  // darkest Shadow Spheres." The Fox line converges out of the dark at
  // every rank — the species verb, same as its attack.
  // "Mischievous pups from birth." A pup does not condense politely —
  // it turns up, having clearly been somewhere it should not have been.
  // The single slash at nothing is the mischief, and it is the same
  // tell the Spectral Tiger Champion uses one rank higher.
  shadow_fox_beb: beb("A Pup From the Depths", P.amethyst, P.violetPale, [
    slash(340, P.violetPale, { ...S, ms: 340, n: 1, r: 0.6, tone: 0 }),
  ]),
  // "Skulking the Shadow Forest, these fox prey on those who seek it."
  shadow_fox_champion: champion("The Skulk", P.amethyst, P.violetPale, [
    motes(340, P.shadow, { ...S, ms: 460, n: 22, r: 2.0, size: 0.07, tone: "in" }),
  ]),
  // "Rulers of Shadow Spheres."
  shadow_fox_lord: lord("A Ruler of Shadow Spheres", P.violetDeep, P.shadow, [
    halo(450, P.amethyst, { ...S, ms: 620, r: 1.0, size: 0.8, spin: 1.2 }),
  ]),

  // "Although small, do not be fooled; these snakes are protectors to
  // the entrance of the Shadow Forest." The Serpent coils on arrival at
  // every rank, tighter each time.
  inverted_serpent_beb: beb("A Small Coil", P.violetIce, P.amethyst, [
    spiral(200, P.amethyst, { ...S, ms: 440, spin: 1.6, r: 0.5, size: 0.8 }),
  ]),
  inverted_serpent_champion: champion("The Guardian Winds", P.amethyst, P.violetIce, [
    spiral(280, P.amethyst, { ...S, ms: 540, spin: 2.2, r: 0.7, size: 1.1 }),
  ]),
  // "The Shadow Forest was and will always be home to these beings;
  // they will protect it at any cost."
  inverted_serpent_lord: lord("At Any Cost", P.amethyst, P.violetIce, [
    spiral(300, P.violetDeep, { ...S, ms: 640, tone: "up", spin: 3, r: 0.85, size: 2.6 }),
  ]),

// ── TIME · MACHINE ─────────────────────────────────────────────────

  // "These demons are responsible for destroying 1% of the Unstable
  // Worlds." The Z4Z line arrives on lightning — its attack is The
  // Sundering and it does not enter politely either.
  z4z_beb: beb("A Demon Sparks", P.time, P.emberGold, [
    bolt(180, P.time, { from: "selfsky", to: "self", ms: 240, r: 0.3 }),
  ]),
  // "Time is a cog in this machine's design to be used for one purpose,
  // although that purpose is unknown."
  z4z_champion: champion("The Cog Turns", P.time, P.rust, [
    bolt(200, P.time, { from: "selfsky", to: "self", ms: 240, r: 0.32 }),
    rune(320, P.rust, { ...S, ms: 520, r: 0.85, n: 8, spin: 1.2 }),
  ]),
  // "No one has ever been able to have a conversation with one of these
  // beings." H'ehll's Sword arrives and the ground gives.
  z4z_lord: lord("H'ehll's Sword Lands", P.time, P.rust, [
    crack(420, P.rust, { ...S, ms: 620, n: 8, r: 2.6 }),
  ]),

  // "Through tremendous effort, these robots have, over time, healed 1%
  // of dying beings." DG-0 is the only line whose arrival is WARM —
  // embers rather than a strike, at every rank.
  dg0_beb: beb("An Angel Descends", P.judgeGold, P.emberGold, [
    embers(220, P.judgeGold, { ...S, ms: 460, n: 16, r: 0.5, size: 0.065, size2: 1.2 }),
  ]),
  // "Farmers of Hope, these beings bring peace and prosperity, if you
  // share their point of view, that is."
  dg0_champion: champion("The Farmer Sows", P.judgeGold, P.emberGold, [
    embers(300, P.judgeGold, { ...S, ms: 520, n: 22, r: 0.7, size: 0.07, size2: 1.6 }),
    rune(360, P.brass, { ...S, ms: 500, r: 0.9, n: 10, spin: 0.4 }),
  ]),
  // "If seen, it's usually in response to an Apocalypse level
  // situation." I'nfini's Shield arrives as a ward, not a threat.
  dg0_lord: lord("I'nfini's Shield", P.judgeGold, P.emberGold, [
    veil(420, P.judgeGold, { ...S, ms: 620, r: 1.5, size: 0.82 }),
  ]),

  // "The most common robot Beb in the Unstable World." Antdroids arrive
  // in NUMBERS — the stream, their species verb, running inward.
  antdroid_beb: beb("One of Many", P.swarm, P.time, [
    stream(160, P.time, { from: "back", to: "self", ms: 460, n: 30, r: 0.22, size: 0.05 }),
  ]),
  // "Born on planet Alloith where oceans are molten metal, this
  // Champion's body is comprised of this metal."
  antdroid_champion: champion("Cast From Molten Metal", P.swarm, P.brass, [
    stream(180, P.brass, { from: "back", to: "self", ms: 520, n: 38, r: 0.24, size: 0.055 }),
    embers(400, P.rust, { ...S, ms: 460, n: 16, r: 0.5, size: 0.065, size2: 1.1 }),
  ]),
  // "It is an Ant's duty to conquer."
  antdroid_lord: lord("The Duty to Conquer", P.swarm, P.brass, [
    lattice(420, P.brass, { ...S, ms: 600, r: 0.95, n: 12, size: 0.05 }),
  ]),

// ── DRAGON · DRAGON ────────────────────────────────────────────────

  // "Rarely seen, these Bebs are born in the Unstable World and have a
  // fondness for Shade." The Lucky Dragon line arrives on a coin at
  // every rank — nothing else in the bestiary gambles on ENTRY.
  lucky_dragon_beb: beb("A Fondness for Shade", P.swarm, P.jynxi, [
    coin(140, P.swarm, { ...S, ms: 520, r: 1.0, size: 0.24, spin: 6 }),
  ]),
  // "These dealers will grant almost any wish, but at what cost?"
  lucky_dragon_champion: champion("The Dealer Sits", P.swarm, P.jynxi, [
    coin(160, P.jynxi, { ...S, ms: 600, r: 1.4, size: 0.27, spin: 8 }),
  ]),
  // "The Greatest Ever, Vavixzu the A'oeternis, was tricked by these
  // Dragons."
  lucky_dragon_lord: lord("The One Who Tricked Vavixzu", P.swarm, P.jynxi, [
    coin(300, P.swarm, { ...S, ms: 700, r: 1.9, size: 0.31, spin: 10 }),
  ]),

  // "The First & Only Leader of the Unstable World, when born, the
  // surrounding area is briefly affected by Bloom." Canon hands over
  // the word: this line arrives in warmth and light, every rank.
  premier_dragon_beb: beb("Born Under Bloom", P.roseSoft, P.emberGold, [
    embers(200, P.emberGold, { ...S, ms: 480, n: 18, r: 0.55, size: 0.07, size2: 1.3 }),
  ]),
  // "As the first dragon that Ascended to Champion, its power is only
  // rivaled by its talent."
  premier_dragon_champion: champion("The First to Ascend", P.roseSoft, P.dragon, [
    embers(280, P.emberGold, { ...S, ms: 540, n: 24, r: 0.7, size: 0.075, size2: 1.6 }),
    star(480, P.dragon, { ...S, ms: 360, r: 1.3, size: 0.045 }),
  ]),
  // "The First & Last, Zavixu the A'aeternis, deemed these dragons
  // Worthy through rigorous trials." A sovereign arrives crowned.
  premier_dragon_lord: lord("Deemed Worthy", P.roseSoft, P.dragon, [
    halo(440, P.goldPale, { ...S, ms: 660, r: 1.1, size: 0.86, spin: 1.0 }),
  ]),

  // "These Bebs bring with their birth a calamity of destruction in the
  // form of Doom." The only line in the game whose ARRIVAL damages the
  // world — the ground cracks under all three, worse each time. Nothing
  // else in the bestiary does this, and it is why a Chaos Dragon
  // landing is the scariest thing on the board.
  chaos_dragon_beb: beb("A Calamity, Newly Born", P.crimson, P.roseSoft, [
    crack(200, P.bloodDeep, { ...S, ms: 460, n: 4, r: 1.4 }),
  ]),
  // "If ever you should be cursed enough to behold this innocent
  // typhoon, know that escape is too late."
  chaos_dragon_champion: champion("The Innocent Typhoon", P.crimson, P.roseSoft, [
    crack(240, P.bloodDeep, { ...S, ms: 560, n: 6, r: 2.2 }),
    spiral(320, P.crimson, { ...S, ms: 520, tone: "up", spin: 2.4, r: 0.6, size: 1.9 }),
  ]),
  // "The One & Only, Xavizu the A'oeternis, in a war with these
  // dragons, admitted that they are a Threat."
  chaos_dragon_lord: lord("A Threat, Admitted", P.crimson, P.bloodDeep, [
    crack(380, P.bloodDeep, { ...S, ms: 700, n: 10, r: 3.4 }),
    spiral(420, P.crimson, { ...S, ms: 600, tone: "up", spin: 3, r: 0.8, size: 2.8 }),
  ]),
};

// ═══════════════════════════════════════════════════════════════════
// PART III.5 — THE TWELVE WOUNDS (H24 pass 3)
// ═══════════════════════════════════════════════════════════════════
// caP'n asked for more passes, and this is the gap the first two left:
// every species has its own ATTACK and none of them has its own WOUND.
//
// H21 keyed the impact to the VICTIM and gave four profiles, one per
// Ilk — and its reasoning is still exactly right and is not being
// overturned: "the attacker's identity is already fully expressed in
// its STRIKE, which it renders itself. The victim's card cannot see who
// hit it without new plumbing." Keying the wound to the victim keeps
// the whole thing derivable from one card's own state diff, so it fires
// identically on every client for free. That stands.
//
// What H21 could not do was say anything SPECIFIC, because there were
// no primitives to say it with. Four Ilk profiles carried the shake
// amplitude and the blood-hue and nothing else. So a Spectral Tiger
// taking a hit and a Shadow Fox taking a hit were the same picture in
// the same colour, which is a strange thing to be true in a game where
// their attacks are The Pounce and Nine Shadows.
//
// ── THE BUDGET IS THE HARD PART, NOT THE IDEAS ─────────────────────
// A bloody turn produces several wounds and a Response Window can
// produce a handful at once, so these are the tightest scores in the
// entire library: **2–3 cues, 300–460ms, no toll, no ring.** They sit
// UNDER H21's existing shake and damage float rather than replacing
// them — the float is still what tells you the number, and this is only
// what tells you whose body it happened to.
//
// If a wound ever needs a fourth cue, it is the wrong wound.
//
// ── AND WHY THERE IS NO SECOND RING ────────────────────────────────
// Every other class in this library ends on a `toll` — the third beat,
// the world acknowledging it. These deliberately do not. A wound is not
// an event the board should stop for; the ATTACK already tolled, a beat
// earlier, and tolling again for the same exchange would double-count
// it. The absence is the discipline.

// A pass-through alias would be pointless, so this one does the job the
// prose above only describes: it CAPS a wound. The budget is the hard
// part of this table — a bloody turn produces several of these and a
// Response Window can produce a handful at once — and a cap in code is
// the only version of that rule that survives someone (me, next month)
// having a great idea for a fourth cue.
//
// 480ms and 3 cues. If a wound needs more, it is the wrong wound.
const hurt = (name, cues, ms) => {
  const capped = cues.slice(0, 3);
  return score(name, capped, Math.min(ms || 460, 480));
};

export const WOUNDS = {

  // ── H-01 · THE LEAK ─────────────────────────────────────────────
  // "Souls fused with technology." Damage a soul-powered suit and what
  // escapes is the soul: light coming out of a body, not blood.
  h01: hurt("The Leak", [
    embers(0, P.starCore, { ...S, ms: 380, n: 12, r: 0.34, size: 0.055, size2: 0.9 }),
    star(60, P.starWhite, { ...S, ms: 280, r: 0.75, size: 0.03, tone: 40 }),
  ], 420),

  // ── H-09 · THE BREACH ───────────────────────────────────────────
  // "Superior suits capable of defending against even Champion level
  // threats" — so when one fails, the failure is ARMOUR. The lattice
  // appears only to come apart, which is the only place in the library
  // that primitive is used destructively.
  h09: hurt("The Breach", [
    lattice(0, P.cyanHot, { ...S, ms: 220, r: 0.42, n: 12, size: 0.03 }),
    shatter(140, P.space, { ...S, ms: 340, n: 7, r: 0.75 }),
  ], 440),

  // ── XULAN · THE OPENING ─────────────────────────────────────────
  // Its own attack is The Incision, where "the wound opens a beat AFTER
  // the blade has already gone." Being on the receiving end is the same
  // idea from the other side: nothing, nothing, and then a line.
  xulan: hurt("The Opening", [
    sever(90, P.scalpel, { ...S, ms: 330, r: 0.9, size: 0.022, size2: 0.14, tone: -10 }),
  ], 420),

  // ── SPECTRAL TIGER · THE FLICKER ────────────────────────────────
  // A Phantom does not bleed; it briefly stops being fully here. Out
  // and then back in, on the same primitive — 180ms apart, which is
  // just long enough to register as a stutter in reality.
  spectral_tiger: hurt("The Flicker", [
    motes(0, P.phantom, { ...S, ms: 260, n: 14, r: 0.7, size: 0.06 }),
    motes(180, P.violetPale, { ...S, ms: 260, n: 14, r: 0.7, size: 0.06, tone: "in" }),
  ], 440),

  // ── SHADOW FOX · THE SCATTER ────────────────────────────────────
  // Nine Shadows converges to bite. Hit the fox and the same dark goes
  // the other way — the species verb, run outward.
  shadow_fox: hurt("The Scatter", [
    motes(0, P.amethyst, { ...S, ms: 380, n: 20, r: 1.0, size: 0.065 }),
    bloom(40, P.violetDeep, { ...S, ms: 220, r: 0.5, tone: 0.6 }),
  ], 420),

  // ── INVERTED SERPENT · SCALES LOST ──────────────────────────────
  // Its Lord Impera is literally Inverted Scales. Hit it and some of
  // them come off — the smallest shatter in the library.
  inverted_serpent: hurt("Scales Lost", [
    shatter(0, P.violetIce, { ...S, ms: 360, n: 8, r: 0.62 }),
    slash(60, P.amethyst, { ...S, ms: 260, n: 1, r: 0.5, tone: 0 }),
  ], 420),

  // ── Z4Z · THE SHORT ─────────────────────────────────────────────
  // A Time machine that "destroys anything for Khao," damaged: it
  // arcs. Two bolts, close together, going nowhere useful.
  z4z: hurt("The Short", [
    bolt(0, P.time, { from: "self", to: "selfsky", ms: 200, r: 0.22 }),
    bolt(110, P.emberGold, { from: "self", to: "selfsky", ms: 170, r: 0.16 }),
    motes(140, P.rust, { ...S, ms: 300, n: 10, r: 0.5, size: 0.05 }),
  ], 460),

  // ── DG-0 · THE DIMMING ──────────────────────────────────────────
  // "Through tremendous effort, these robots have healed 1% of dying
  // beings." The only wound in the game that is not violent: a light
  // going down. The rune runs `close` — the same shape as Fallen — and
  // there is no debris at all.
  dg0: hurt("The Dimming", [
    rune(0, P.brass, { ...S, ms: 380, r: 0.6, n: 6, spin: 0.5, tone: "close" }),
    embers(60, P.judgeGold, { ...S, ms: 300, n: 10, r: 0.3, size: 0.05, size2: 0.7 }),
  ], 440),

  // ── ANTDROID · PARTS ────────────────────────────────────────────
  // "The most common robot Beb in the Unstable World." There are always
  // more. Losing some of itself is a maintenance event, not a tragedy —
  // so this is the flattest, least dramatic wound in the twelve.
  antdroid: hurt("Parts", [
    shatter(0, P.brass, { ...S, ms: 340, n: 9, r: 0.66 }),
  ], 380),

  // ── LUCKY DRAGON · THE HOUSE WINS ───────────────────────────────
  // Every card on this line is a wager. Getting hit is the one bet it
  // lost, so the coin comes up and lands — low, fast, and without the
  // long hang that makes its own gambles exciting. The joke is that the
  // primitive is identical and the TIMING is what makes it a loss.
  lucky_dragon: hurt("The House Wins", [
    coin(0, P.shadow, { ...S, ms: 380, r: 0.5, size: 0.19, spin: 3 }),
  ], 420),

  // ── PREMIER DRAGON · THE AFFRONT ────────────────────────────────
  // "The First & Only Leader of the Unstable World." It is not hurt so
  // much as OFFENDED. A short hard bloom and a single mark — the
  // shortest recoil in the twelve, because a sovereign does not dwell.
  premier_dragon: hurt("The Affront", [
    bloom(0, P.roseSoft, { ...S, ms: 240, r: 0.72 }),
    glyph(60, P.dragon, { ...S, ms: 300, tone: "✦", size: 20, r: 0.3 }),
  ], 380),

  // ── CHAOS DRAGON · THE ANSWER ───────────────────────────────────
  // "A calamity of destruction in the form of Doom." Hitting one is a
  // bad idea and the wound says so: the only wound in the game where
  // the GROUND takes damage instead of the card. Nothing about this
  // reads as the Chaos Dragon being in trouble.
  chaos_dragon: hurt("The Answer", [
    crack(0, P.bloodDeep, { ...S, ms: 400, n: 5, r: 1.3 }),
    bloom(40, P.crimson, { ...S, ms: 240, r: 0.8 }),
  ], 460),
};

/**
 * The wound this card shows when it takes Lyf damage — its SPECIES', not
 * its attacker's. Total: an Escling, or anything minted next year,
 * returns null and keeps H21's shake-and-float exactly as it was.
 */
export const woundFor = (card) => (card ? WOUNDS[lineOf(card)] || null : null);

// ═══════════════════════════════════════════════════════════════════
// PART IV — THE SEEDING
// ═══════════════════════════════════════════════════════════════════
// The four Esclings. "Primordial essence waiting to be Transcended."
//
// H21 gave them the humblest arrival in the language — it RISES from
// beneath the felt, slow, no fanfare, because "an Escling is a promise."
// Three are placed before the duel even begins, so this score is seen
// at the very start of every single match, by every player, forever.
//
// It is therefore the most-viewed animation in Terror and it is
// deliberately the quietest. No ring, no mark, no light from the sky.
// Something comes up out of the ground and has not decided what it is
// yet. Each takes its own Ilk's hue and nothing else changes — four
// promises, in four colours.

// `own` is the promise this particular essence is making. The shared
// part stays shared and stays the loudest thing here — an Escling has
// not decided what it is yet, and all four must read as the same kind
// of not-yet. What differs is one quiet cue hinting which way it will
// go, which is the only honest amount of foreshadowing available to a
// card whose entire character is that it hasn't happened yet.
const seeding = (name, ilk, own = []) => {
  const hue = ilkHue(ilk);
  return score(name, [
    embers(0, hue, { ...S, ms: 620, n: 18, r: 0.42, size: 0.06, size2: 1.0 }),
    ...own,
    bloom(280, hue, { ...S, ms: 340, r: 0.62, tone: 0.7 }),
    shock(320, P.bone, { ...S, ms: 420, size: 0.14, r: 0.9, tone: 0.5 }),
  ], 780);
};

export const ESCLING_SUMMONS = {
  // Space: one point of light, very small. Every Space line in the game
  // ends up at a star; this is the first hint of one.
  escling_space: seeding("The Seeding · Space", "Space", [
    star(200, P.starCore, { ...S, ms: 300, r: 0.5, size: 0.028 }),
  ]),
  // Phantom: it is already slightly not-there. The only Seeding with
  // `tone: "in"` on it, because the Phantom lines are the ones whose
  // whole identity is arriving from somewhere you weren't watching.
  escling_phantom: seeding("The Seeding · Phantom", "Phantom", [
    motes(180, P.violetPale, { ...S, ms: 400, n: 12, r: 0.55, size: 0.055, tone: "in" }),
  ]),
  // Dragon: warmth under the felt before anything shows. A Premier
  // Dragon's birth brings Bloom and a Chaos Dragon's brings Doom — at
  // Escling stage it is only heat, and which one it becomes is not
  // decided yet.
  escling_dragon: seeding("The Seeding · Dragon", "Dragon", [
    bloom(160, P.roseSoft, { ...S, ms: 320, r: 0.45, tone: 0.5 }),
  ]),
  // Time: the smallest rune in the game. A machine is a made thing, and
  // this is the first mark of its making.
  escling_time: seeding("The Seeding · Time", "Time", [
    rune(170, P.brass, { ...S, ms: 380, r: 0.42, n: 4, spin: 0.8 }),
  ]),
};

// ═══════════════════════════════════════════════════════════════════
// PART V — THE RITE OF RETURN  (H23 Ship 1 · unchanged)
// ═══════════════════════════════════════════════════════════════════
// caP'n, verbatim: "Every being will not have its own animation for
// rezing; the most basic level of Resurrection will be the classic
// skulls etc graveyard looking animation... the variations should be
// basically the same just maybe slightly different colors or way
// different and no skulls put something else (sometimes) don't
// overthink this one."
//
// ONE rite, four tints, and the mark is the only thing that changes.
// Phantom keeps the skull because a Phantom returning IS the classic
// image; Dragon keeps it because a dragon's bones are the loudest thing
// it leaves behind. Space gets a star (nothing dies in the Void, it
// just goes out) and Time gets the Sorcery mark (a Time being was never
// gone, it was elsewhen).
//
// R44/R77: every resurrection method marks the tally. This score does
// not care WHICH — the ground gives it back the same way whether a
// Ritual Tome paid, a Time stamp paid, or the player did.

const riteCues = (mark, griefColor, lightColor) => ([
  grave(0, griefColor, { ...S, ms: 940, n: 7, r: 0.88, size: 27, tone: mark }),
  pillar(210, lightColor, { ...S, ms: 560, r: 3.8, size: 0.42, spin: 0.35 }),
  bloom(540, P.returnCore, { ...S, ms: 340, r: 1.2 }),
  shock(570, griefColor, { ...S, ms: 520, size: 0.2, r: 1.9 }),
  motes(590, lightColor, { ...S, ms: 480, n: 26, r: 1.7, size: 0.07, tone: "in" }),
]);

// ⚠ THESE THREE SHARE A SHAPE ON PURPOSE AND MUST KEEP SHARING IT.
// A uniqueness audit flags base/Phantom/Dragon as identical geometry in
// different tints, and that is caP'n's own July decree working exactly
// as intended: "the variations should be basically the same just maybe
// slightly different colors."
//
// They are also, as of pass 5, no longer reachable by any card in
// `cards.js` — all 36 beings resolve through `RETURNS` above. This
// table is now purely the FALLBACK for a line minted later, which is
// the one job it should have. Do not "fix" the duplication; it is the
// floor, and a floor is supposed to be flat.
export const RITE_OF_RETURN = {
  base:    score("The Rite of Return", riteCues("☠", P.griefViolet, P.returnGreen), 1120),
  Phantom: score("The Rite of Return · Phantom", riteCues("☠", P.phantom, P.returnGreen), 1120),
  Dragon:  score("The Rite of Return · Dragon", riteCues("☠", P.dragon, P.emberGold), 1120),
  Space:   score("The Rite of Return · Space", riteCues("✧", P.space, P.starWhite), 1120),
  Time:    score("The Rite of Return · Time", riteCues("❋", P.time, P.emberGold), 1120),
};

// ═══════════════════════════════════════════════════════════════════
// PART V.5 — THE TWELVE RETURNS (H24 pass 5)
// ═══════════════════════════════════════════════════════════════════
// caP'n, Aug 3: "do we have cool unique animations for affinities
// resurrections etc? if not build them here."
//
// He was right to ask. An audit of the library found every other class
// at 100% distinct shapes — 36/36 Imperas, 72/72 Tomes, 12/12 attacks,
// 12/12 wounds — and the Rite sitting at **five entries covering all
// thirty-six beings**, three of which were the same shape in different
// tints. It was the last place in the game where two different cards
// produced the same picture.
//
// ── THIS DOES NOT OVERTURN THE JULY DECREE, IT COMPLETES IT ────────
// caP'n, July 27: "the most basic level of Resurrection will be the
// classic skulls etc graveyard looking animation... the variations
// should be basically the same just maybe slightly different colors or
// way different and no skulls put something else (sometimes) don't
// overthink this one."
//
// Every word of that still holds and is visible in all twelve below:
// the GRAVE marks open every single one, the pillar and the green core
// are shared, and the whole shape stays recognisable as one rite. What
// each species gets is the middle beat — ONE cue that says how THIS
// thing comes back — plus its own mark, which he explicitly asked for
// ("no skulls put something else (sometimes)").
//
// So: same rite, twelve returns. Not twelve bespoke resurrections —
// that is the thing he told me not to build, and he was right.
//
// ── AND THE FALLBACK IS UNTOUCHED ─────────────────────────────────
// `RITE_OF_RETURN` above still exists and still answers for anything
// that isn't one of the twelve lines. A being minted next year gets its
// Ilk tint, exactly as it did in H23.

const rite = (name, mark, grief, light, own = []) => score(name, [
  grave(0, grief, { ...S, ms: 900, n: 7, r: 0.88, size: 27, tone: mark }),
  pillar(210, light, { ...S, ms: 560, r: 3.8, size: 0.42, spin: 0.35 }),
  ...own,
  bloom(540, P.returnCore, { ...S, ms: 340, r: 1.2 }),
  shock(570, grief, { ...S, ms: 520, size: 0.2, r: 1.9 }),
  motes(590, light, { ...S, ms: 460, n: 26, r: 1.7, size: 0.07, tone: "in" }),
], 1160);

export const RETURNS = {

  // "Souls fused with technology." The suit is intact; what left was
  // the soul, so what comes back is a star reigniting inside it. Its
  // own wound is The Leak — this is that, run backward.
  h01: rite("The Star Reignites", "✧", P.space, P.starWhite, [
    star(380, P.starWhite, { ...S, ms: 420, r: 1.6, size: 0.05 }),
  ]),

  // "Self-proclaimed Generals of the stars." A soldier does not rise —
  // it REBOOTS. The armour comes back first and the being after it.
  h09: rite("Systems Restored", "✧", P.space, P.cyanHot, [
    lattice(340, P.cyanHot, { ...S, ms: 480, r: 0.6, n: 12, size: 0.04 }),
  ]),

  // "A race of assassins that drift through the Void." A Xulan never
  // arrives through the ground — it steps out of somewhere else, which
  // is how all three of its summons work too. The one rite in the
  // twelve where the being is not lifted but LET BACK IN.
  xulan: rite("Back Through the Void", "✧", P.space, P.scalpel, [
    rift(320, P.scalpel, { ...S, ms: 520, r: 1.5, size: 0.034, size2: 0.28 }),
  ]),

  // "Kings of the Spirit Worlds." Death is not an obstacle to a thing
  // that already lives in the Spirit World — it simply re-condenses,
  // and the Spirit World is what hands it back.
  spectral_tiger: rite("The Spirit World Returns It", "☠", P.phantom, P.violetPale, [
    motes(320, P.violetPale, { ...S, ms: 480, n: 30, r: 1.9, size: 0.075, tone: "in" }),
  ]),

  // "Born at the Bottomless Depths of the darkest Shadow Spheres." The
  // dark that scattered when it was wounded comes back and re-forms it.
  // The Fox's verb, both directions, at both ends of its life.
  shadow_fox: rite("The Dark Reassembles", "☠", P.amethyst, P.violetDeep, [
    plume(300, P.shadow, { ...S, ms: 520, n: 26, r: 0.9, size: 0.1, size2: 0.9, tone: 0.6 }),
  ]),

  // "They will protect the Shadow Forest at any cost." Including this
  // one. It winds back up out of the ground — the coil, re-formed.
  inverted_serpent: rite("It Coils Back", "☠", P.amethyst, P.violetIce, [
    spiral(310, P.amethyst, { ...S, ms: 540, tone: "up", spin: 2.4, r: 0.62, size: 2.0 }),
  ]),

  // "These demons are responsible for destroying 1% of the Unstable
  // Worlds." It does not come back gently and it does not come back
  // grateful. The only rite that arcs.
  z4z: rite("It Restarts", "❋", P.time, P.rust, [
    bolt(300, P.time, { from: "selfsky", to: "self", ms: 240, r: 0.32 }),
    bolt(400, P.emberGold, { from: "selfsky", to: "self", ms: 200, r: 0.2 }),
  ]),

  // "Through tremendous effort, these robots have healed 1% of dying
  // beings in the Unstable World." It has spent its whole existence
  // doing this FOR other people. The gentlest return in the game, and
  // the only one where nothing is dug up, torn, or struck — it simply
  // warms back into being.
  dg0: rite("It Heals Itself", "❋", P.judgeGold, P.emberGold, [
    embers(300, P.judgeGold, { ...S, ms: 560, n: 24, r: 0.65, size: 0.07, size2: 1.7 }),
  ]),

  // "It is an Ant's duty to conquer. Since the initial unfolding of
  // Time, they have, and until the final folding of time, they will."
  // There are always more. The swarm arrives and rebuilds it, which is
  // the least sentimental resurrection in Terror and exactly right.
  antdroid: rite("The Swarm Rebuilds It", "❋", P.time, P.swarm, [
    stream(300, P.brass, { from: "back", to: "self", ms: 540, n: 44, r: 0.24, size: 0.055 }),
  ]),

  // "These dealers will grant almost any wish, but at what cost?"
  // It comes back on a BET. The coin is thrown over its own grave and
  // the rite does not resolve until it lands — the only resurrection in
  // the game that looks, for a moment, like it might not work.
  lucky_dragon: rite("It Wagers Its Way Back", "⚄", P.jynxi, P.swarm, [
    coin(280, P.swarm, { ...S, ms: 620, r: 1.5, size: 0.27, spin: 8 }),
  ]),

  // "When born, the surrounding area is briefly affected by Bloom."
  // Canon says a Premier Dragon's ARRIVAL brings Bloom — so its return
  // brings it again. The dawn, a second time.
  premier_dragon: rite("Bloom, Again", "☠", P.dragon, P.roseSoft, [
    embers(300, P.emberGold, { ...S, ms: 540, n: 26, r: 0.75, size: 0.075, size2: 1.8 }),
  ]),

  // "A calamity of destruction in the form of Doom." It is not GIVEN
  // back. It comes back, and the ground objects — the only rite in the
  // twelve where the felt takes damage on the way out.
  chaos_dragon: rite("It Claws Its Way Out", "☠", P.crimson, P.bloodDeep, [
    // Written first with a crack AND a thorn — and the linter counted 7
    // things live at 590ms, which is the smear this file's own budget
    // rule exists to prevent. The honest cut is the crack: a fissure is
    // damage done TO the floor, and this card is something coming UP
    // through it. Two cues both saying "the ground" is the second idea
    // ONE IMAGE PER SCORE forbids, and the thorn is the better one.
    thorn(300, P.crimson, { ...S, ms: 500, n: 8, r: 1.05, size: 0.9 }),
  ]),
};

/**
 * How this being comes back. Species first, then its School's tint,
 * then the base rite. Total at every step — a card minted next year
 * still resurrects, it just does it in its School's colour like it did
 * in H23.
 *
 * RITE_OF_RETURN is keyed Phantom/Dragon/Space/Time — the SCHOOL axis
 * under the 2026-08-25 ruling — so this reads `card.school`. Those four
 * words lived in `card.ilk` until that date's field-name fix; the tint
 * a given being gets is unchanged.
 */
export const returnFor = (card) => {
  if (!card) return RITE_OF_RETURN.base;
  return RETURNS[lineOf(card)] || RITE_OF_RETURN[card.school] || RITE_OF_RETURN.base;
};

// ── THE CODEX VIEW ─────────────────────────────────────────────────
export const describeBestiary = () => {
  const rows = [];
  const push = (channel, reg) => Object.keys(reg).forEach((k) =>
    rows.push({ channel, key: k, name: reg[k].name, ms: reg[k].ms, cues: reg[k].cues.length }));
  push("LYT ATTACK", LYT_ATTACKS);
  push("WOUND", WOUNDS);
  push("SUMMON", SUMMONS);
  push("SEEDING", ESCLING_SUMMONS);
  push("RETURN", RETURNS);
  push("RETURN · TINT", RITE_OF_RETURN);
  return rows;
};

const beingMotion = {
  lineOf, LYT_ATTACKS, SUMMONS, ESCLING_SUMMONS, RITE_OF_RETURN,
  WOUNDS, woundFor, RETURNS, returnFor, describeBestiary,
};
export default beingMotion;
